import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UrlApi } from "../../../../services/apirest";
import { getData } from "../../../../services/library";
import {
    esFlagActivo,
    esPaisArgentina,
    nuevaUnidadNegocioIdFila,
    normalizarUnidadesNegocio,
    textoOGuion,
} from "./unidadesNegocioUtils";

const cellSx = {
    fontSize: "0.8rem",
    py: 0.9,
    px: 1,
};

const draftVacio = {
    razon_social: "",
    direccion: "",
    id_pais: "",
    id_provincia: "",
    id_localidad: "",
};

/**
 * @param {{
 *   otrasUnidadesNegocio: unknown,
 *   unidades: unknown,
 *   paises?: Array<{ id_pais?: number, pais?: string }>,
 *   disabled?: boolean,
 *   onOtrasChange?: (checked: boolean) => void,
 *   onUnidadesChange?: (next: Array<Record<string, unknown>>) => void,
 * }} props
 */
export default function UnidadesNegocioSection({
    otrasUnidadesNegocio,
    unidades,
    paises = [],
    disabled = true,
    onOtrasChange,
    onUnidadesChange,
}) {
    const addBlockRef = useRef(null);
    const [provincias, setProvincias] = useState([]);
    const [localidadesPorId, setLocalidadesPorId] = useState({});
    const [draftLocalidades, setDraftLocalidades] = useState([]);
    const [draft, setDraft] = useState(draftVacio);
    const [draftError, setDraftError] = useState(null);
    const [editingIdFila, setEditingIdFila] = useState(null);

    const filas = useMemo(() => normalizarUnidadesNegocio(unidades), [unidades]);
    const checkOtras = esFlagActivo(otrasUnidadesNegocio);
    const editable = !disabled;

    useEffect(() => {
        getData("/provincias", (data) => setProvincias(Array.isArray(data) ? data : []));
    }, []);

    const idsProvinciaKey = useMemo(() => {
        const ids = new Set();
        filas.forEach((u) => {
            if (u.id_provincia != null && u.id_provincia !== "") {
                ids.add(Number(u.id_provincia));
            }
        });
        return Array.from(ids)
            .filter((id) => Number.isFinite(id))
            .sort((a, b) => a - b)
            .join(",");
    }, [filas]);

    useEffect(() => {
        if (!idsProvinciaKey) {
            setLocalidadesPorId({});
            return undefined;
        }
        const ids = idsProvinciaKey.split(",").map((id) => Number(id));
        let cancelled = false;
        Promise.all(
            ids.map((id) =>
                fetch(`${UrlApi}/localidades/prov/${id}`)
                    .then((r) => r.json())
                    .then((list) => (Array.isArray(list) ? list : []))
                    .catch(() => [])
            )
        ).then((listas) => {
            if (cancelled) return;
            const map = {};
            listas.forEach((list) => {
                list.forEach((loc) => {
                    const id = loc?.id ?? loc.id_localidad;
                    if (id != null) {
                        map[Number(id)] = loc.localidad;
                    }
                });
            });
            setLocalidadesPorId(map);
        });
        return () => {
            cancelled = true;
        };
    }, [idsProvinciaKey]);

    const draftEsArgentina = esPaisArgentina(draft.id_pais, paises);

    useEffect(() => {
        if (!draftEsArgentina || draft.id_provincia === "" || draft.id_provincia == null) {
            setDraftLocalidades([]);
            return undefined;
        }
        let cancelled = false;
        fetch(`${UrlApi}/localidades/prov/${draft.id_provincia}`)
            .then((r) => r.json())
            .then((list) => {
                if (!cancelled) setDraftLocalidades(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (!cancelled) setDraftLocalidades([]);
            });
        return () => {
            cancelled = true;
        };
    }, [draftEsArgentina, draft.id_provincia]);

    const provinciasPorId = useMemo(() => {
        const m = new Map();
        provincias.forEach((p) => {
            const id = p?.id ?? p.id_provincia;
            if (id != null) m.set(Number(id), p.provincia);
        });
        return m;
    }, [provincias]);

    const paisesPorId = useMemo(() => {
        const m = new Map();
        paises.forEach((p) => {
            if (p?.id_pais != null) m.set(Number(p.id_pais), p.pais);
        });
        return m;
    }, [paises]);

    const etiquetaPais = (unidad) => {
        if (unidad.pais) return textoOGuion(unidad.pais);
        return textoOGuion(paisesPorId.get(Number(unidad.id_pais)));
    };

    const etiquetaProvincia = (unidad) => {
        if (unidad.provincia) return textoOGuion(unidad.provincia);
        if (unidad.id_provincia == null || unidad.id_provincia === "") return "—";
        return textoOGuion(provinciasPorId.get(Number(unidad.id_provincia)));
    };

    const etiquetaLocalidad = (unidad) => {
        if (unidad.localidad) return textoOGuion(unidad.localidad);
        if (unidad.id_localidad == null || unidad.id_localidad === "") return "—";
        return textoOGuion(localidadesPorId[Number(unidad.id_localidad)]);
    };

    const resetDraft = useCallback(() => {
        setDraft(draftVacio);
        setDraftError(null);
        setEditingIdFila(null);
    }, []);

    const nombrePais = (idPais) => paisesPorId.get(Number(idPais)) || "";
    const nombreProvincia = (idProvincia) => provinciasPorId.get(Number(idProvincia)) || "";
    const nombreLocalidad = (idLocalidad, lista) => {
        const fromMap = localidadesPorId[Number(idLocalidad)];
        if (fromMap) return fromMap;
        const fromList = (lista || []).find(
            (l) => Number(l.id ?? l.id_localidad) === Number(idLocalidad)
        );
        return fromList?.localidad || "";
    };

    const handleAgregarFila = () => {
        const razon = draft.razon_social.trim();
        const direccion = draft.direccion.trim();
        const idPais = draft.id_pais === "" ? NaN : Number(draft.id_pais);
        if (!razon) {
            setDraftError("Ingresá la razón social");
            return;
        }
        if (!direccion) {
            setDraftError("Ingresá la dirección fiscal");
            return;
        }
        if (!Number.isFinite(idPais)) {
            setDraftError("Seleccioná un país");
            return;
        }
        const argentina = esPaisArgentina(idPais, paises);
        const idProvincia =
            draft.id_provincia === "" ? null : Number(draft.id_provincia);
        const idLocalidad =
            draft.id_localidad === "" ? null : Number(draft.id_localidad);
        if (argentina) {
            if (!Number.isFinite(idProvincia)) {
                setDraftError("Seleccioná una provincia");
                return;
            }
            if (!Number.isFinite(idLocalidad)) {
                setDraftError("Seleccioná una localidad");
                return;
            }
        }

        const fila = {
            razon_social: razon,
            direccion,
            id_pais: idPais,
            pais: nombrePais(idPais),
            id_provincia: argentina && Number.isFinite(idProvincia) ? idProvincia : null,
            provincia: argentina ? nombreProvincia(idProvincia) : "",
            id_localidad: argentina && Number.isFinite(idLocalidad) ? idLocalidad : null,
            localidad: argentina ? nombreLocalidad(idLocalidad, draftLocalidades) : "",
        };

        if (editingIdFila) {
            const actual = filas.find((f) => f.idFila === editingIdFila);
            onUnidadesChange?.(
                filas.map((f) =>
                    f.idFila === editingIdFila
                        ? { ...actual, ...fila, idFila: editingIdFila, id: actual?.id }
                        : f
                )
            );
        } else {
            onUnidadesChange?.([...filas, { ...fila, idFila: nuevaUnidadNegocioIdFila() }]);
        }
        resetDraft();
    };

    const handleEditar = (fila) => {
        setEditingIdFila(fila.idFila);
        setDraft({
            razon_social: String(fila.razon_social ?? ""),
            direccion: String(fila.direccion ?? ""),
            id_pais: fila.id_pais != null && fila.id_pais !== "" ? String(fila.id_pais) : "",
            id_provincia:
                fila.id_provincia != null && fila.id_provincia !== ""
                    ? String(fila.id_provincia)
                    : "",
            id_localidad:
                fila.id_localidad != null && fila.id_localidad !== ""
                    ? String(fila.id_localidad)
                    : "",
        });
        setDraftError(null);
        addBlockRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    const handleEliminar = (idFila) => {
        onUnidadesChange?.(filas.filter((f) => f.idFila !== idFila));
        if (editingIdFila === idFila) resetDraft();
    };

    return (
        <Box sx={{ mt: 0.5, px: 1, pb: 1 }}>
            <FormGroup sx={{ mb: checkOtras ? 0.5 : 0 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            disabled={disabled}
                            checked={checkOtras}
                            onChange={(e) => {
                                if (disabled || !onOtrasChange) return;
                                onOtrasChange(e.target.checked);
                            }}
                        />
                    }
                    label={
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                            Otras unidades de negocio
                        </Typography>
                    }
                />
            </FormGroup>

            {checkOtras ? (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, ml: 0.5 }}>
                        Agregá otras razones sociales y domicilios fiscales de la empresa.
                    </Typography>
                    <TableContainer
                        sx={{
                            border: "1px solid #e8ecf0",
                            borderRadius: 1,
                            bgcolor: "#fafbfc",
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Razón social</TableCell>
                                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Dirección fiscal</TableCell>
                                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>País</TableCell>
                                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Provincia</TableCell>
                                    <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Localidad</TableCell>
                                    {editable ? (
                                        <TableCell sx={{ ...cellSx, fontWeight: 700 }} align="center">
                                            Acciones
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={editable ? 6 : 5} sx={cellSx}>
                                            <Typography variant="body2" color="text.secondary">
                                                {editable
                                                    ? "No hay unidades de negocio cargadas. Usá el formulario de abajo para agregar la primera."
                                                    : "No hay unidades de negocio cargadas."}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filas.map((unidad, idx) => (
                                        <TableRow key={unidad.idFila || `unidad-${idx}`}>
                                            <TableCell sx={cellSx}>{textoOGuion(unidad.razon_social)}</TableCell>
                                            <TableCell sx={cellSx}>{textoOGuion(unidad.direccion)}</TableCell>
                                            <TableCell sx={cellSx}>{etiquetaPais(unidad)}</TableCell>
                                            <TableCell sx={cellSx}>{etiquetaProvincia(unidad)}</TableCell>
                                            <TableCell sx={cellSx}>{etiquetaLocalidad(unidad)}</TableCell>
                                            {editable ? (
                                                <TableCell sx={cellSx} align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        aria-label="Editar fila"
                                                        onClick={() => handleEditar(unidad)}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        aria-label="Eliminar fila"
                                                        onClick={() => handleEliminar(unidad.idFila)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            ) : null}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {editable ? (
                        <Box
                            ref={addBlockRef}
                            sx={{
                                mt: 1.5,
                                pt: 1,
                                borderTop: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mb: 1 }}
                            >
                                {editingIdFila ? "Editando fila seleccionada" : "Nueva fila"}
                            </Typography>
                            <Grid container spacing={1.5} alignItems="flex-start">
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        required
                                        label="Razón social"
                                        autoComplete="off"
                                        value={draft.razon_social}
                                        onChange={(e) => {
                                            setDraft((prev) => ({ ...prev, razon_social: e.target.value }));
                                            setDraftError(null);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        required
                                        label="Dirección fiscal"
                                        autoComplete="off"
                                        value={draft.direccion}
                                        onChange={(e) => {
                                            setDraft((prev) => ({ ...prev, direccion: e.target.value }));
                                            setDraftError(null);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel>País</InputLabel>
                                        <Select
                                            label="País"
                                            value={draft.id_pais}
                                            onChange={(e) => {
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    id_pais: e.target.value,
                                                    id_provincia: "",
                                                    id_localidad: "",
                                                }));
                                                setDraftError(null);
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Seleccionar</em>
                                            </MenuItem>
                                            {paises.map((p) => (
                                                <MenuItem key={p.id_pais} value={String(p.id_pais)}>
                                                    {p.pais}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {draftEsArgentina ? (
                                    <>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl fullWidth size="small" required>
                                                <InputLabel>Provincia</InputLabel>
                                                <Select
                                                    label="Provincia"
                                                    value={draft.id_provincia}
                                                    onChange={(e) => {
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            id_provincia: e.target.value,
                                                            id_localidad: "",
                                                        }));
                                                        setDraftError(null);
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Seleccionar</em>
                                                    </MenuItem>
                                                    {provincias.map((p) => {
                                                        const id = p.id ?? p.id_provincia;
                                                        return (
                                                            <MenuItem key={id} value={String(id)}>
                                                                {p.provincia}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <FormControl fullWidth size="small" required>
                                                <InputLabel>Localidad</InputLabel>
                                                <Select
                                                    label="Localidad"
                                                    value={draft.id_localidad}
                                                    onChange={(e) => {
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            id_localidad: e.target.value,
                                                        }));
                                                        setDraftError(null);
                                                    }}
                                                    disabled={!draft.id_provincia}
                                                >
                                                    <MenuItem value="">
                                                        <em>Seleccionar</em>
                                                    </MenuItem>
                                                    {draftLocalidades.map((loc) => {
                                                        const id = loc.id ?? loc.id_localidad;
                                                        return (
                                                            <MenuItem key={id} value={String(id)}>
                                                                {loc.localidad}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </>
                                ) : null}
                                <Grid
                                    item
                                    xs={12}
                                    md={draftEsArgentina ? 4 : 12}
                                    sx={{ display: "flex", alignItems: "flex-start", pt: { md: 0.5 } }}
                                >
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={handleAgregarFila}
                                    >
                                        {editingIdFila ? "Guardar" : "Agregar"}
                                    </Button>
                                    {editingIdFila ? (
                                        <Button size="small" onClick={resetDraft} sx={{ ml: 1 }}>
                                            Cancelar
                                        </Button>
                                    ) : null}
                                </Grid>
                            </Grid>
                            {draftError ? (
                                <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                                    {draftError}
                                </FormHelperText>
                            ) : null}
                        </Box>
                    ) : null}
                </>
            ) : null}
        </Box>
    );
}
